/**
 * Manufacturer warranties, and the two certifications they count toward
 * (ADR 0309).
 *
 * TAMKO judges Pro Platinum on how many enhanced warranties the company
 * registers in its programme year, and how many of those were MasterShield.
 * GAF judges Master Elite on squares: how many squares of Silver or Golden
 * Pledge were registered, and how many squares of GAF product were bought.
 * Both programmes count by the date the warranty was registered (Jonathon,
 * 2026-10-24: "registered date").
 *
 * Which tier a roof can carry is read off its material order in
 * `warrantyParts.ts`.
 *
 * Nothing here touches the fee the estimate charges — that is
 * `autoExpenses.ts`'s, by the estimate's own tier. A registered warranty's
 * fee is what the receipt says.
 */

import {
  GOLDEN_PLEDGE_PER_SQUARE,
  SILVER_PLEDGE_PER_SQUARE,
  TAMKO_WARRANTY_PER_SQUARE,
  WARRANTY_REGISTRATION_PER_SQUARE,
} from "./autoExpenses.js";
import { type Cents } from "./money.js";

export type WarrantyManufacturer = "TAMKO" | "GAF";

export const REGISTERED_TIERS = [
  "tamko_mastershield",
  "tamko_proshield",
  "gaf_golden_pledge",
  "gaf_silver_pledge",
  "gaf_system_plus",
] as const;
export type RegisteredTier = (typeof REGISTERED_TIERS)[number];

export interface RegisteredTierFacts {
  manufacturer: WarrantyManufacturer;
  name: string;
  /** Qualifying accessory categories the roof must carry. */
  accessories: number;
  /** What registering costs per square, where it is known. */
  perSquare: Cents | null;
}

export const REGISTERED_TIER: Record<RegisteredTier, RegisteredTierFacts> = {
  tamko_mastershield: {
    manufacturer: "TAMKO",
    name: "TAMKO MasterShield",
    accessories: 4,
    perSquare: TAMKO_WARRANTY_PER_SQUARE,
  },
  tamko_proshield: {
    manufacturer: "TAMKO",
    name: "TAMKO ProShield",
    accessories: 3,
    perSquare: WARRANTY_REGISTRATION_PER_SQUARE,
  },
  gaf_golden_pledge: {
    manufacturer: "GAF",
    name: "GAF Golden Pledge",
    accessories: 5,
    perSquare: GOLDEN_PLEDGE_PER_SQUARE,
  },
  gaf_silver_pledge: {
    manufacturer: "GAF",
    name: "GAF Silver Pledge",
    accessories: 4,
    perSquare: SILVER_PLEDGE_PER_SQUARE,
  },
  gaf_system_plus: {
    manufacturer: "GAF",
    name: "GAF System Plus",
    accessories: 3,
    perSquare: null,
  },
};

export function tierLabel(tier: string | null | undefined): string {
  if (!tier) return "—";
  return REGISTERED_TIER[tier as RegisteredTier]?.name ?? tier;
}

/** The tiers one manufacturer files, best first. */
export function tiersFor(manufacturer: WarrantyManufacturer): RegisteredTier[] {
  return REGISTERED_TIERS.filter(
    (tier) => REGISTERED_TIER[tier].manufacturer === manufacturer,
  );
}

/**
 * The warranty an estimate's own tier (`estimates.warranty`) would register.
 *
 * The estimate's codes predate this module: `tamko` is the full TAMKO system
 * and `registration` the three-part roof (ADR 0256), so they are MasterShield
 * and ProShield by another name.
 */
const ESTIMATE_TIER: Readonly<Record<string, RegisteredTier>> = {
  tamko: "tamko_mastershield",
  registration: "tamko_proshield",
  silver_pledge: "gaf_silver_pledge",
  golden_pledge: "gaf_golden_pledge",
};

export function registeredTierOfEstimate(
  warranty: string | null | undefined,
): RegisteredTier | null {
  return (warranty && ESTIMATE_TIER[warranty]) || null;
}

export function manufacturerOfEstimate(
  warranty: string | null | undefined,
): WarrantyManufacturer | null {
  const tier = registeredTierOfEstimate(warranty);
  return tier ? REGISTERED_TIER[tier].manufacturer : null;
}

/** The estimate's own code a registered tier is charged under, if any. */
export function estimateWarrantyOf(tier: RegisteredTier): string | null {
  for (const [code, registered] of Object.entries(ESTIMATE_TIER)) {
    if (registered === tier) return code;
  }
  return null;
}

/**
 * Whether the Register Warranty step opens the warranty form.
 *
 * A TAMKO or GAF roof does, and so does a job that already has a warranty
 * record, so the record can be corrected. Anything else (gutters, siding,
 * another manufacturer's warranty) is one press that ticks the step or marks
 * it N/A, as it always was. Jonathon, 2026-10-27: "6. a".
 */
export function opensWarrantyForm(job: {
  manufacturer: WarrantyManufacturer | null;
  recorded: boolean;
}): boolean {
  return job.recorded || job.manufacturer !== null;
}

/* ------------------------------------------------------------------------ */
/* The programmes                                                            */
/* ------------------------------------------------------------------------ */

export const TAMKO_PROGRAM = {
  name: "TAMKO Pro Platinum",
  /** The day TAMKO made the company Pro Platinum; the year runs from it. */
  start: "2026-09-21",
  /** Enhanced warranties a year, MasterShield and ProShield together. */
  total: 24,
  /** Of which at least this many MasterShield. */
  masterShieldMin: 10,
} as const;

export const GAF_PROGRAM = {
  name: "GAF Master Elite",
  /** The company's start with GAF; the year is prorated from it (Jonathon). */
  start: "2026-08-05",
  /** Squares of Silver or Golden Pledge a year. */
  pledgeSquares: 500,
  /** Squares of GAF shingles and accessories bought a year. */
  rewardsSquares: 1000,
} as const;

export interface ProgramYear {
  from: string;
  to: string;
  /** How much of the year has gone, 0 to 1. */
  elapsed: number;
  daysGone: number;
  daysTotal: number;
}

const DAY_MS = 86_400_000;
const dayNumber = (date: string): number =>
  Math.floor(Date.parse(`${date}T00:00:00Z`) / DAY_MS);

/**
 * The programme year `today` falls in: from the most recent anniversary of
 * the start date, for a year.
 *
 * Before the start date the first year is still the year, with none of it
 * gone — a report run the week before enrolment has nothing to prorate.
 */
export function programYear(start: string, today: string): ProgramYear {
  const [, month, day] = start.split("-");
  let year = Number(today.slice(0, 4));
  let from = `${year}-${month}-${day}`;
  if (from > today) {
    year -= 1;
    from = `${year}-${month}-${day}`;
  }
  if (from < start) from = start;
  const to = `${Number(from.slice(0, 4)) + 1}${from.slice(4)}`;
  const daysTotal = dayNumber(to) - dayNumber(from);
  const daysGone = Math.min(
    Math.max(dayNumber(today) - dayNumber(from) + 1, 0),
    daysTotal,
  );
  return {
    from,
    to,
    elapsed: daysTotal > 0 ? daysGone / daysTotal : 0,
    daysGone,
    daysTotal,
  };
}

export interface Pace {
  done: number;
  target: number;
  /** What the year's pace says should be done by today. */
  expectedByNow: number;
  onTrack: boolean;
  remaining: number;
  /** Where the year ends up at today's rate. Null before the year starts. */
  projected: number | null;
}

/**
 * Whether `done` keeps up with `target` spread evenly over the year.
 *
 * Judged against the whole warranty the pace has earned by today, rounded
 * down: two warranties thirty-four days into a 24-a-year programme is on
 * track, because the pace has only earned two so far.
 */
export function paceOf(done: number, target: number, year: ProgramYear): Pace {
  const expectedByNow = target * year.elapsed;
  return {
    done,
    target,
    expectedByNow,
    onTrack: done >= Math.floor(expectedByNow),
    remaining: Math.max(target - done, 0),
    projected: year.elapsed > 0 ? Math.round(done / year.elapsed) : null,
  };
}

/** A registered warranty, as much of it as the scorecards read. */
export interface WarrantyCount {
  status: string;
  manufacturer: string | null;
  tier: string | null;
  squares: number | string | null;
  registered_on: string | null;
}

export interface ScoreBar {
  key: string;
  label: string;
  unit: "warranties" | "squares";
  pace: Pace;
  /** Rows counted toward the bar, for the screen to list. */
  note?: string;
}

export interface Scorecard {
  name: string;
  year: ProgramYear;
  bars: ScoreBar[];
  /** The whole card's verdict: every bar on track. */
  onTrack: boolean;
}

function inYear(
  rows: readonly WarrantyCount[],
  manufacturer: WarrantyManufacturer,
  year: ProgramYear,
): WarrantyCount[] {
  return rows.filter(
    (row) =>
      row.status === "registered" &&
      row.manufacturer === manufacturer &&
      row.registered_on !== null &&
      row.registered_on >= year.from &&
      row.registered_on < year.to,
  );
}

const squaresOf = (rows: readonly WarrantyCount[]): number =>
  rows.reduce((sum, row) => {
    const n = Number(row.squares ?? 0);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);

/** TAMKO Pro Platinum: 24 enhanced warranties a year, 10 of them MasterShield. */
export function tamkoScorecard(
  rows: readonly WarrantyCount[],
  today: string,
): Scorecard {
  const year = programYear(TAMKO_PROGRAM.start, today);
  const counted = inYear(rows, "TAMKO", year);
  const master = counted.filter((r) => r.tier === "tamko_mastershield").length;
  const pro = counted.filter((r) => r.tier === "tamko_proshield").length;
  const total: ScoreBar = {
    key: "total",
    label: "Enhanced warranties",
    unit: "warranties",
    pace: paceOf(counted.length, TAMKO_PROGRAM.total, year),
  };
  const masterShield: ScoreBar = {
    key: "mastershield",
    label: "MasterShield (all 4 TAMKO accessories)",
    unit: "warranties",
    pace: paceOf(master, TAMKO_PROGRAM.masterShieldMin, year),
  };
  const bars: ScoreBar[] = [
    total,
    masterShield,
    {
      key: "proshield",
      label: "ProShield (3 of 4)",
      unit: "warranties",
      pace: paceOf(
        pro,
        TAMKO_PROGRAM.total - TAMKO_PROGRAM.masterShieldMin,
        year,
      ),
      note: "The rest of the 24 may be ProShield; more MasterShield is never a problem.",
    },
  ];
  return {
    name: TAMKO_PROGRAM.name,
    year,
    bars,
    // ProShield is a ceiling, not a target: only the two real requirements
    // decide the badge.
    onTrack: total.pace.onTrack && masterShield.pace.onTrack,
  };
}

/**
 * GAF Master Elite: 500 squares of Silver or Golden Pledge a year and 1,000
 * rewards squares of GAF product.
 *
 * Rewards squares are not registered by the company — ABC Supply and
 * Heely-Brown report what they sell to GAF — so the figure passed in is an
 * estimate read off the material orders of completed GAF jobs, and the bar
 * says so. Null leaves the bar at zero.
 */
export function gafScorecard(
  rows: readonly WarrantyCount[],
  rewardsSquares: number | null,
  today: string,
): Scorecard {
  const year = programYear(GAF_PROGRAM.start, today);
  const counted = inYear(rows, "GAF", year).filter(
    (r) => r.tier === "gaf_silver_pledge" || r.tier === "gaf_golden_pledge",
  );
  const bars: ScoreBar[] = [
    {
      key: "pledge",
      label: "Silver / Golden Pledge squares",
      unit: "squares",
      pace: paceOf(
        Math.round(squaresOf(counted) * 100) / 100,
        GAF_PROGRAM.pledgeSquares,
        year,
      ),
    },
    {
      key: "rewards",
      label: "GAF rewards squares (estimated from orders)",
      unit: "squares",
      pace: paceOf(
        Math.round((rewardsSquares ?? 0) * 100) / 100,
        GAF_PROGRAM.rewardsSquares,
        year,
      ),
      note: "Counted from the shingle bundles on completed GAF jobs' material orders. The suppliers register the product with GAF.",
    },
  ];
  return {
    name: GAF_PROGRAM.name,
    year,
    bars,
    onTrack: bars.every((bar) => bar.pace.onTrack),
  };
}

/** "On track" or "Behind pace", for a badge. */
export function paceWords(onTrack: boolean): string {
  return onTrack ? "On track" : "Behind pace";
}

/* ------------------------------------------------------------------------ */
/* Still to register                                                         */
/* ------------------------------------------------------------------------ */

/** A job whose Register Warranty step is still open, as the page reads it. */
export interface PendingRegistration<J> {
  job: J | null;
  /** When All Work Complete was ticked; null while the work is not done. */
  completedAt: string | null;
  /** The active estimate's tier (`estimates.warranty`). */
  estimateWarranty: string | null;
}

/** A completed job whose warranty is still to be registered. */
export interface RegistrationDue<J> {
  job: J;
  completedOn: string;
  manufacturer: WarrantyManufacturer;
  tier: RegisteredTier | null;
}

/**
 * The jobs to register next, oldest completion first: the work is complete,
 * the step is still open, and the estimate was priced at a TAMKO or GAF
 * warranty. A job on any other roof has nothing to register with either
 * programme, so it is not listed.
 */
export function readyToRegister<J>(
  pending: readonly PendingRegistration<J>[],
): RegistrationDue<J>[] {
  const due: RegistrationDue<J>[] = [];
  for (const row of pending) {
    if (!row.job || !row.completedAt) continue;
    const manufacturer = manufacturerOfEstimate(row.estimateWarranty);
    if (!manufacturer) continue;
    due.push({
      job: row.job,
      completedOn: row.completedAt.slice(0, 10),
      manufacturer,
      tier: registeredTierOfEstimate(row.estimateWarranty),
    });
  }
  return due.sort((a, b) => a.completedOn.localeCompare(b.completedOn));
}
